/**
 * Offline Support Hook
 *
 * Provides offline capability for field workers (Promotoras).
 * Implements RFP requirements for:
 * - Offline data access
 * - Background sync for pending actions
 * - Connection status monitoring
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '@/lib/authFetch';
import { isOffline as isVerifiedOffline, subscribeConnectivity } from '@/lib/offline/connectivity';

// ============================================================================
// TYPES
// ============================================================================

export interface PendingAction {
  id: string;
  type: 'create' | 'update' | 'delete';
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: any;
  createdAt: string;
  retryCount: number;
  maxRetries: number;
  priority: 'high' | 'normal' | 'low';
}

export interface SyncStatus {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  lastSyncAt: Date | null;
  syncError: string | null;
}

export interface OfflineData<T> {
  key: string;
  data: T;
  cachedAt: string;
  expiresAt?: string;
  version?: number;
}

// ============================================================================
// STORAGE UTILITIES
// ============================================================================

const PENDING_ACTIONS_KEY = 'weavein_pending_actions';
const OFFLINE_DATA_PREFIX = 'weavein_offline_';
const LAST_SYNC_KEY = 'weavein_last_sync';

function getPendingActions(): PendingAction[] {
  if (typeof window === 'undefined') return [];
  const stored = localStorage.getItem(PENDING_ACTIONS_KEY);
  if (!stored) return [];
  try {
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

function savePendingActions(actions: PendingAction[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PENDING_ACTIONS_KEY, JSON.stringify(actions));
}

function getOfflineData<T>(key: string): OfflineData<T> | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(`${OFFLINE_DATA_PREFIX}${key}`);
  if (!stored) return null;
  try {
    const data = JSON.parse(stored) as OfflineData<T>;
    // Check if expired
    if (data.expiresAt && new Date(data.expiresAt) < new Date()) {
      localStorage.removeItem(`${OFFLINE_DATA_PREFIX}${key}`);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function saveOfflineData<T>(
  key: string,
  data: T,
  ttlMinutes?: number
): void {
  if (typeof window === 'undefined') return;
  const now = new Date();
  const offlineData: OfflineData<T> = {
    key,
    data,
    cachedAt: now.toISOString(),
    expiresAt: ttlMinutes
      ? new Date(now.getTime() + ttlMinutes * 60 * 1000).toISOString()
      : undefined,
  };
  localStorage.setItem(`${OFFLINE_DATA_PREFIX}${key}`, JSON.stringify(offlineData));
}

function clearOfflineData(key: string): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(`${OFFLINE_DATA_PREFIX}${key}`);
}

function getAllOfflineDataKeys(): string[] {
  if (typeof window === 'undefined') return [];
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(OFFLINE_DATA_PREFIX)) {
      keys.push(key.replace(OFFLINE_DATA_PREFIX, ''));
    }
  }
  return keys;
}

// ============================================================================
// CONNECTION STATUS HOOK
// ============================================================================

export function useOnlineStatus(): boolean {
  // Deliberately NOT `navigator.onLine`. That flag false-negatives in the
  // desktop webview and behind some VPNs, and it used to be the only thing
  // behind the "You're offline" banner — users sat under a permanent offline
  // warning with a working connection. `isOffline()` is only true once a probe
  // of the API has actually failed. See lib/offline/connectivity.
  const [isOnline, setIsOnline] = useState(() => !isVerifiedOffline());

  useEffect(() => {
    return subscribeConnectivity((offline) => setIsOnline(!offline));
  }, []);

  return isOnline;
}

// ============================================================================
// OFFLINE DATA HOOK
// ============================================================================

export interface UseOfflineDataOptions<T> {
  key: string;
  fetchFn: () => Promise<T>;
  ttlMinutes?: number;
  onError?: (error: Error) => void;
}

export interface UseOfflineDataReturn<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  isFromCache: boolean;
  cachedAt: Date | null;
  refresh: () => Promise<void>;
  clearCache: () => void;
}

export function useOfflineData<T>(
  options: UseOfflineDataOptions<T>
): UseOfflineDataReturn<T> {
  const { key, fetchFn, ttlMinutes = 60, onError } = options;

  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [cachedAt, setCachedAt] = useState<Date | null>(null);

  const isOnline = useOnlineStatus();

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    // Try to get cached data first
    const cached = getOfflineData<T>(key);
    if (cached) {
      setData(cached.data);
      setIsFromCache(true);
      setCachedAt(new Date(cached.cachedAt));
    }

    // If online, fetch fresh data
    if (isOnline) {
      try {
        const freshData = await fetchFn();
        setData(freshData);
        setIsFromCache(false);
        saveOfflineData(key, freshData, ttlMinutes);
        setCachedAt(new Date());
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch data');
        setError(error);
        onError?.(error);

        // If we have cached data, use it despite the error
        if (cached) {
          setData(cached.data);
          setIsFromCache(true);
        }
      }
    } else if (!cached) {
      // Offline with no cache
      setError(new Error('No cached data available offline'));
    }

    setIsLoading(false);
  }, [key, fetchFn, ttlMinutes, isOnline, onError]);

  const refresh = useCallback(async () => {
    await loadData();
  }, [loadData]);

  const clearCache = useCallback(() => {
    clearOfflineData(key);
    setIsFromCache(false);
    setCachedAt(null);
  }, [key]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    data,
    isLoading,
    error,
    isFromCache,
    cachedAt,
    refresh,
    clearCache,
  };
}

// ============================================================================
// PENDING ACTIONS HOOK
// ============================================================================

export interface UsePendingActionsReturn {
  pendingActions: PendingAction[];
  addPendingAction: (
    action: Omit<PendingAction, 'id' | 'createdAt' | 'retryCount'>
  ) => string;
  removePendingAction: (id: string) => void;
  syncPendingActions: () => Promise<void>;
  isSyncing: boolean;
  syncError: string | null;
}

export function usePendingActions(): UsePendingActionsReturn {
  const [pendingActions, setPendingActions] = useState<PendingAction[]>(() =>
    getPendingActions()
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const isOnline = useOnlineStatus();
  const syncInProgressRef = useRef(false);

  // Persist pending actions
  useEffect(() => {
    savePendingActions(pendingActions);
  }, [pendingActions]);

  /**
   * Add a new pending action
   */
  const addPendingAction = useCallback(
    (action: Omit<PendingAction, 'id' | 'createdAt' | 'retryCount'>): string => {
      const id = `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const newAction: PendingAction = {
        ...action,
        id,
        createdAt: new Date().toISOString(),
        retryCount: 0,
      };

      setPendingActions((prev) => {
        // Sort by priority
        const priorityOrder = { high: 0, normal: 1, low: 2 };
        const updated = [...prev, newAction].sort(
          (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]
        );
        return updated;
      });

      return id;
    },
    []
  );

  /**
   * Remove a pending action
   */
  const removePendingAction = useCallback((id: string) => {
    setPendingActions((prev) => prev.filter((a) => a.id !== id));
  }, []);

  /**
   * Sync all pending actions
   */
  const syncPendingActions = useCallback(async () => {
    if (!isOnline || syncInProgressRef.current || pendingActions.length === 0) {
      return;
    }

    syncInProgressRef.current = true;
    setIsSyncing(true);
    setSyncError(null);

    const failedActions: PendingAction[] = [];

    for (const action of pendingActions) {
      try {
        const response = await authFetch(action.endpoint, {
          method: action.method,
          jsonBody: action.data,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        // Action succeeded, remove from pending
        removePendingAction(action.id);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';

        if (action.retryCount < action.maxRetries) {
          // Increment retry count and keep in pending
          failedActions.push({
            ...action,
            retryCount: action.retryCount + 1,
          });
        } else {
          // Max retries exceeded
          setSyncError(`Failed to sync action: ${errorMessage}`);
          failedActions.push(action);
        }
      }
    }

    // Update pending actions with failed ones
    setPendingActions(failedActions);

    // Save last sync time
    localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString());

    setIsSyncing(false);
    syncInProgressRef.current = false;
  }, [isOnline, pendingActions, removePendingAction]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && pendingActions.length > 0) {
      syncPendingActions();
    }
  }, [isOnline, pendingActions.length, syncPendingActions]);

  return {
    pendingActions,
    addPendingAction,
    removePendingAction,
    syncPendingActions,
    isSyncing,
    syncError,
  };
}

// ============================================================================
// COMBINED OFFLINE HOOK
// ============================================================================

export interface UseOfflineReturn {
  // Connection status
  isOnline: boolean;

  // Sync status
  syncStatus: SyncStatus;

  // Data caching
  cacheData: <T>(key: string, data: T, ttlMinutes?: number) => void;
  getCachedData: <T>(key: string) => T | null;
  clearCachedData: (key: string) => void;
  getAllCachedKeys: () => string[];

  // Pending actions
  queueAction: (
    action: Omit<PendingAction, 'id' | 'createdAt' | 'retryCount'>
  ) => string;
  cancelAction: (id: string) => void;
  syncNow: () => Promise<void>;
}

export function useOffline(): UseOfflineReturn {
  const isOnline = useOnlineStatus();
  const {
    pendingActions,
    addPendingAction,
    removePendingAction,
    syncPendingActions,
    isSyncing,
    syncError,
  } = usePendingActions();

  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(() => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(LAST_SYNC_KEY);
    return stored ? new Date(stored) : null;
  });

  const syncStatus: SyncStatus = {
    isOnline,
    isSyncing,
    pendingCount: pendingActions.length,
    lastSyncAt,
    syncError,
  };

  const cacheData = useCallback(
    <T>(key: string, data: T, ttlMinutes?: number) => {
      saveOfflineData(key, data, ttlMinutes);
    },
    []
  );

  const getCachedData = useCallback(<T>(key: string): T | null => {
    const cached = getOfflineData<T>(key);
    return cached?.data ?? null;
  }, []);

  const clearCachedData = useCallback((key: string) => {
    clearOfflineData(key);
  }, []);

  const getAllCachedKeys = useCallback(() => {
    return getAllOfflineDataKeys();
  }, []);

  const syncNow = useCallback(async () => {
    await syncPendingActions();
    setLastSyncAt(new Date());
  }, [syncPendingActions]);

  return {
    isOnline,
    syncStatus,
    cacheData,
    getCachedData,
    clearCachedData,
    getAllCachedKeys,
    queueAction: addPendingAction,
    cancelAction: removePendingAction,
    syncNow,
  };
}

export default useOffline;
